import { FC, Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Context } from "../../../..";
// import { ISubTaskView } from "../../../../models/TaskModels/ISubTask";
import "../../../styles/table.pc.css";
import "./SubTasksGraph.css";
import { Link } from "react-router-dom";
import TaskStatus from "../../../Shared/TaskStatus";
import TaskPriority from "../../../Shared/TaskPriority";
import PersonLineDisplay from "../../../Shared/PersonLineDisplay";
import { fullNameFormat } from "../../../../helpers/Inicials";
import { dateToNiceString } from "../../../../helpers/dateToNiceString";
import ArrowDown from "../../../../assets/arrowDown.svg";
import ArrowRight from "../../../../assets/ArrowRight.svg";
import SubTasks from "../../../../assets/subtasks.svg";
import LineConnector from "./LineConnector";
import TaskType from "../../../Shared/TaskType";
import UserPopupWrapper from "../../../Shared/UserPopup/UserPopupWrapper";
import Tooltip from "../../../UiLib/CustomTooltip/CustomTooltip";
import { ISubTaskView } from "../../../../common/api_interfaces/task/ISubTaskView";

const LEVEL_COLORS = ["#F97316", "#699F4D", "#C25ADB", "#6974EB", "#C9D6FC", "#F5D5E0", "#F7EBFC"];

interface SubTasksGraphProps {
    taskId: number;
    treeHead?: ISubTaskView;
}

const SubTasksGraph: FC<SubTasksGraphProps> = ({ taskId, treeHead }) => {
    const [foldedTaskIds, setFoldedTaskIds] = useState<Set<number>>(new Set());
    const [graphOffset, setGraphOffset] = useState<[number, number]>([0, 0]);
    const [graphParentHeight, setGraphParentHeight] = useState<number>(0);
    const tableRef = useRef<HTMLDivElement>(null);
    const [graphUpdateFlag, setGraphUpdateFlag] = useState<boolean>(true);

    const foldTask = useCallback((taskId: number) => {
        setFoldedTaskIds(prev => new Set(prev).add(taskId));
        setGraphUpdateFlag(!graphUpdateFlag);
    }, []);

    const unfoldTask = useCallback((taskId: number) => {
        setFoldedTaskIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(taskId);
            return newSet;
        });
        setGraphUpdateFlag(!graphUpdateFlag);
    }, []);

    const isFolded = useCallback((id: number) => foldedTaskIds.has(id), [foldedTaskIds]);

    const updateDimensions = useCallback(() => {
        if (!tableRef.current) return;

        const parentRect = tableRef.current.getBoundingClientRect();
        setGraphOffset([-parentRect.left, -parentRect.top]);
        setGraphParentHeight(parentRect.height - 20);
    }, [])

    useEffect(() => {
        if (!tableRef.current) return;

        updateDimensions();

        const observer = new ResizeObserver(updateDimensions);
        observer.observe(tableRef.current);

        return () => observer.disconnect();
    }, [tableRef]);

    const renderTask = useCallback((index: number, task: ISubTaskView, level: number, parentTaskId?: number): ReactNode => {
        const hasSubtasks = task.subTasks.length > 0;
        const folded = isFolded(task.id);
        const color = LEVEL_COLORS[level % LEVEL_COLORS.length];

        return (
            <Fragment key={`task-${index}`}>
                <tr className={`subtasks-row ${task.id === taskId ? "highlighted" : ""}`}>
                    <td style={{ width: "fit-content", minWidth: `${100 + 15 * level}px` }}>
                        <div style={{ display: "flex", alignItems: "center", height: "fit-content" }}>
                            {parentTaskId && (
                                <LineConnector
                                    offset={graphOffset}
                                    parentHeight={graphParentHeight}
                                    sourceId={`subtask-${parentTaskId}`}
                                    targetId={`subtask-${task.id}`}
                                    updateFlag={graphUpdateFlag}
                                />
                            )}

                            <div style={{ flexShrink: 0, display: "flex", marginLeft: `${15 * level}px` }}>
                                <div>
                                    <svg width="10" height="10" style={{ verticalAlign: "baseline", marginRight: "8px" }}>
                                        <circle id={`subtask-${task.id}`} cx="4" cy="4" r="4" fill="#E1E7EA" />
                                    </svg>
                                </div>
                                {hasSubtasks ? (
                                    <button
                                        className="subtask-hide-button"
                                        onClick={() => folded ? unfoldTask(task.id) : foldTask(task.id)}
                                        aria-label={folded ? "Развернуть" : "Свернуть"}
                                    >
                                        <img
                                            alt="arrow"
                                            width="20px"
                                            style={{ marginLeft: "-10px" }}
                                            src={folded ? ArrowDown : ArrowRight}
                                        />
                                    </button>
                                ) : (
                                    <div style={{ width: "20px", marginLeft: "-10px" }} />
                                )}
                                <div className="subtask-number-block">
                                    <div className="subtask-number-block-cur">
                                        <svg width="10" height="10" style={{ verticalAlign: "baseline", padding: "1px" }}>
                                            <circle cx="4" cy="4" r="4" fill={color} />
                                        </svg>

                                        <Link to={`/task/${task.id}`} className="subtask-number-text">
                                            {task.number}
                                        </Link>
                                    </div>
                                    {hasSubtasks && (
                                        <div className="subtask-number-text-block">
                                            <span className="subtask-subtasks-number-text">{task.subTasks.length}</span>
                                            <img src={SubTasks} alt="subtasks" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </td>
                    <td style={{ width: "fit-content", maxWidth: "200px", paddingRight: "10px" }}>
                        <Tooltip text={task.name ?? ""} offset={5} placement="bottom">
                            <Link to={`/task/${task.id}`} className="subtask-name">
                                {task.name}
                            </Link>
                        </Tooltip>
                    </td>
                    <td style={{ width: "fit-content", maxWidth: "200px", paddingRight: "10px" }}>
                        <Tooltip text={task.board.name ?? ""} offset={5} placement="bottom">
                            <Link to={`/board/${task.board.id}`} className="subtask-board">
                                {task.board.name}
                            </Link>
                        </Tooltip>
                    </td>
                    <td style={{ paddingRight: "10px" }}>
                        <TaskStatus
                            className="subtask-status"
                            name={task.status.name}
                            color={JSON.parse(task.status.style).color}
                        />
                    </td>
                    <td style={{ paddingRight: "10px" }}>
                        <TaskPriority
                            className="subtask-priority"
                            name={task.priority.name}
                            color={JSON.parse(task.priority.style).color}
                            icon={JSON.parse(task.priority.style).icon}
                        />
                    </td>
                    <td style={{ paddingRight: "10px" }}>
                        <TaskType
                            name={task.type.name}
                            color={JSON.parse(task.type.style).color}
                            icon={JSON.parse(task.type.style).icon}
                        />
                    </td>
                    <td style={{ paddingRight: "10px" }}>
                        {task.responsible && (
                            <UserPopupWrapper userId={task.responsible.id}>
                                <PersonLineDisplay
                                    name={fullNameFormat(
                                        {
                                            name: task.responsible.name,
                                            surname: task.responsible.surname,
                                            middlename: task.responsible.middlename,
                                        },
                                        "s N M"
                                    )}
                                    userId={task.responsible.id}
                                />
                            </UserPopupWrapper>
                        )}
                    </td>
                    <td style={{ paddingRight: "10px", minWidth: "100px" }}>
                        {task.endDate ? dateToNiceString(new Date(task.endDate), "dd.MM.yyyy") : "-"}
                    </td>
                </tr>
                {!folded && task.subTasks.map((subTask, index) =>
                    renderTask(index + 1, subTask, level + 1, task.id)
                )}
            </Fragment>
        );
    }, [taskId, graphOffset, graphParentHeight, isFolded, foldTask, unfoldTask]);

    const tableHeaders = useMemo(() => (
        <tr>
            <th><div style={{ marginLeft: "20px" }}>№</div></th>
            <th>НАЗВАНИЕ</th>
            <th>ДОСКА</th>
            <th>СТАТУС</th>
            <th>ПРИОРИТЕТ</th>
            <th>ТИП</th>
            <th>ИСПОЛНИТЕЛЬ</th>
            <th>ДАТА</th>
        </tr>
    ), []);

    return (
        <div style={{ position: "relative", overflowX: "scroll", height: "100%", padding: "10px 0px 5px" }} ref={tableRef}>
            <table className="subtasks-table" style={{ width: "100%", overflow: "scroll" }}>
                <thead>{tableHeaders}</thead>
                <tbody>{treeHead && renderTask(0, treeHead, 0)}</tbody>
            </table>
        </div>
    );
};

export default SubTasksGraph;